import { isCancel } from "axios";
export const NO_HTTP_STATUS = null;

// Session failures reach the console through the 200 envelope: token missing/invalid/expired,
// device conflict and the shared handler's session code.
export const AUTH_FAILURE_CODES: ReadonlySet<number> = new Set([90002, 90003, 90006, 90008, 44004]);

export const RATE_LIMIT_CODE = 5000;

export const HTTP_UNAUTHORIZED = 401;

export function isAuthFailure(status?: number | null, code?: number | null) {
  return status === HTTP_UNAUTHORIZED || (typeof code === "number" && AUTH_FAILURE_CODES.has(code));
}

export interface ApiErrorOptions {
  status?: number | null;
  code?: number | null;
  details?: unknown;
  retryable?: boolean;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly name = "ApiError";
  readonly status: number | null;
  readonly code: number | null;
  readonly details: unknown;
  private readonly retryableOverride: boolean | undefined;

  constructor(
    message: string,
    { status = NO_HTTP_STATUS, code = null, details, retryable, cause }: ApiErrorOptions = {}
  ) {
    super(message, { cause });
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryableOverride = retryable;
  }

  get isClientError() {
    const hasServerStatus = this.status !== null && this.status >= 500;
    return (
      (this.status !== null && this.status >= 400 && this.status < 500) || (this.code !== null && !hasServerStatus)
    );
  }

  get isRetryable() {
    return this.retryableOverride ?? (this.status !== null && this.status >= 500);
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) return error;
  if (error instanceof Error) return new ApiError(error.message, { cause: error, retryable: false });
  return new ApiError(String(error), { details: error, retryable: false });
}

// Authentication teardown already redirects and cancels sibling requests.
export function isAuthCancellation(error: unknown) {
  return isCancel(error) || (error instanceof ApiError && isAuthFailure(error.status, error.code));
}
